// Rewards.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { getRewards } from '../api/rewards';
import { getRewardsComingSoonOverlayEnabled } from '../api/settings';
import RewardCard from '../components/RewardCard';
import './Rewards.css';

const Rewards = () => {
  const { userData } = useContext(AuthContext);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMyRewards, setShowMyRewards] = useState(false);
  const [showComingSoonOverlay, setShowComingSoonOverlay] = useState(false);
  
  // Use userData.tier as the source of truth (default to tier 1)
  const userTier = userData?.tier || 1;

  useEffect(() => {
    fetchRewards();
  }, []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      const enabled = await getRewardsComingSoonOverlayEnabled();
      if (isMounted) setShowComingSoonOverlay(enabled);
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const data = await getRewards();
      setRewards(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load rewards');
      console.error('Error fetching rewards:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterRewards = () => {
    let filtered = rewards.filter(reward => reward.active);

    if (showMyRewards) {
      filtered = filtered.filter(reward => 
        (userData?.redeemedRewards || []).includes(reward.id)
      );
    }

    return filtered;
  };

  const filteredRewards = filterRewards();

  if (loading) {
    return (
      <div className="page rewards-page">
        {showComingSoonOverlay && (
          <div className="rewards-coming-soon-overlay" role="status" aria-live="polite">
            <div className="rewards-coming-soon-content">
              <div className="rewards-coming-soon-ar" dir="rtl">
                ترقبوا الاعلان عن العروض والخصومات غدا في الموقع
              </div>
              <div className="rewards-coming-soon-en" dir="ltr">
                Stay tuned — offers and discounts will be announced tomorrow on the website.
              </div>
            </div>
          </div>
        )}
        <div className="loading">Loading rewards...</div>
      </div>
    );
  }

  return (
    <div className="page rewards-page">
      {showComingSoonOverlay && (
        <div className="rewards-coming-soon-overlay" role="status" aria-live="polite">
          <div className="rewards-coming-soon-content">
            <div className="rewards-coming-soon-ar" dir="rtl">
              ترقبوا الاعلان عن العروض والخصومات غدا في الموقع
            </div>
            <div className="rewards-coming-soon-en" dir="ltr">
              Stay tuned — offers and discounts will be announced tomorrow on the website.
            </div>
          </div>
        </div>
      )}
      <div className="rewards-container">
        {/* Header Section */}
        <div className="rewards-header">
          <div className="header-content">
            <div className="title-section">
              <h1>Promotions</h1>
              <p className="rewards-subtitle">Exclusive discounts for KOS members</p>
            </div>
            <div className="tier-badge">
              <span className="tier-label">Your Tier</span>
              <span className="tier-value">{userTier}</span>
            </div>
          </div>
        </div>

        {/* Simple Tab Navigation */}
        <div className="rewards-tabs">
          <button 
            className={`tab-btn ${!showMyRewards ? 'active' : ''}`}
            onClick={() => setShowMyRewards(false)}
          >
            All Offers
          </button>
          <button 
            className={`tab-btn ${showMyRewards ? 'active' : ''}`}
            onClick={() => setShowMyRewards(true)}
          >
            My Rewards
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {/* Rewards Grid */}
        {filteredRewards.length === 0 ? (
          <div className="no-rewards">
            <div className="no-rewards-icon">🎁</div>
            <p>{showMyRewards ? 'No redeemed rewards yet' : 'No rewards available'}</p>
            {showMyRewards && <p className="no-rewards-hint">Start redeeming offers to see them here</p>}
          </div>
        ) : (
          <div className="rewards-grid">
            {filteredRewards.map(reward => (
              <RewardCard 
                key={reward.id} 
                reward={reward}
                userTier={userTier}
                isRedeemed={(userData?.redeemedRewards || []).includes(reward.id)}
                onRedeemSuccess={fetchRewards}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Rewards;
